import { Element as SlateElement, Transforms, Editor as SlateEditor, Node } from 'slate';
import { CustomElement, Formatting } from 'slate/types/slate';

type Styling = { alignment?: string; indent?: boolean; format?: string | null; class?: string; [key: string]: unknown };

const formattingOf = (el: CustomElement): Formatting | undefined => ('formatting' in el ? el.formatting : undefined);

export const isBlockActive = (editor: SlateEditor, format: string, styling?: Styling) => {
  const [match] = SlateEditor.nodes(editor, {
    match: (n: Node) => {
      if (!SlateElement.isElement(n)) return false;
      if (n.type !== format) return false;
      const fmt = formattingOf(n);
      if (styling?.alignment) {
        return fmt?.alignment === styling.alignment;
      }
      if (styling?.indent) {
        return !!fmt?.indent;
      }
      if (styling?.format) {
        return fmt?.format === styling.format;
      }
      return true;
    },
  });
  return !!match;
};

export const isSimpleBlockActive = (editor: SlateEditor, format: string) => {
  const [match] = SlateEditor.nodes(editor, {
    match: (n: Node) => SlateElement.isElement(n) && n.type === format,
  });

  return !!match;
};

export const isMarkActive = (editor: SlateEditor, format: string) => {
  const marks = SlateEditor.marks(editor);
  return marks ? (marks as Record<string, unknown>)[format] === true : false;
};

export const toggleMark = (editor: SlateEditor, format: string) => {
  const isActive = isMarkActive(editor, format);

  if (isActive) {
    SlateEditor.removeMark(editor, format);
  } else {
    SlateEditor.addMark(editor, format, true);
  }
};

export const toggleBlock = (editor: SlateEditor, format: string, styling?: Styling) => {
  if (!editor.selection) return;

  const structuralFormatToType: Record<string, string> = {
    li: 'list-item',
    ul: 'bulleted-list',
    ol: 'numbered-list',
  };
  const targetType = styling?.format ? structuralFormatToType[styling.format] : undefined;
  if (styling && targetType) {
    const at = editor.lastSelection || editor.selection;
    const entries = Array.from(
      SlateEditor.nodes(editor, {
        at,
        match: (n: Node) => SlateElement.isElement(n) && n.type === targetType,
      })
    );
    if (entries.length === 0) return;

    const allHaveClass = entries.every(([n]) => (SlateElement.isElement(n) ? formattingOf(n)?.class : undefined) === styling.class);
    for (const [node, path] of entries) {
      const current = (SlateElement.isElement(node) ? formattingOf(node) : undefined) ?? {};
      // formatting bag is loosely shaped here (Styling.indent is boolean vs Formatting.indent number)
      const next: Record<string, unknown> = { ...current };
      if (allHaveClass) {
        delete next.class;
      } else {
        next.class = styling.class;
      }
      Transforms.setNodes(editor, { formatting: next } as Partial<CustomElement>, { at: path });
    }
    return;
  }

  const parentNode = Node.parent(editor, editor.selection.anchor.path);
  const textFormatting: Record<string, unknown> = (SlateElement.isElement(parentNode) ? formattingOf(parentNode) : undefined) ?? {};
  let customFormatting: Record<string, unknown> = {};

  if (!!styling) {
    const isIndentPress = !!styling['indent'];
    const isAlreadyIndented = !!textFormatting['indent'];

    let shouldBeIndented = isAlreadyIndented;

    if (isIndentPress) {
      shouldBeIndented = !isAlreadyIndented;
    }

    const isBlockQuotePress = styling['format'] === 'blockquote';
    const isAlreadyBlockQuote = textFormatting.format === 'blockquote';

    let shouldBeBlockQuote = isAlreadyBlockQuote;

    if (isBlockQuotePress) {
      shouldBeBlockQuote = !shouldBeBlockQuote;
    }

    customFormatting = {
      ...textFormatting,
      ...styling,
      indent: shouldBeIndented,
    };

    if (!styling.class) {
      delete customFormatting.class;
    }

    if ('format' in styling && !styling.format) {
      delete customFormatting.format;
    }

    if (shouldBeBlockQuote) {
      customFormatting.format = 'blockquote';
    } else {
      if (isAlreadyBlockQuote) {
        customFormatting.format = null;
      }
    }
  }

  const newProperties = {
    type: format,
    formatting: customFormatting,
  };

  Transforms.setNodes(editor, newProperties as Partial<CustomElement>, {
    at: editor.lastSelection ?? undefined,
  });
};

const HEADING_FORMATS = new Set(['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'heading-1', 'heading-2', 'heading-3', 'heading-one', 'heading-two', 'heading-three']);

const isHeading = (node: Node | undefined): boolean =>
  !!node && SlateElement.isElement(node) && (HEADING_FORMATS.has(formattingOf(node)?.format ?? '') || HEADING_FORMATS.has(node.type));

const lastHeadingText = (node: Node | undefined): string => {
  if (!node) return '';
  const children = SlateElement.isElement(node) ? node.children : [];
  for (let i = children.length - 1; i >= 0; i--) {
    const text = lastHeadingText(children[i]);
    if (text) return text;
  }
  if (isHeading(node)) {
    const text = Node.string(node).trim();
    if (text) return text;
  }
  return '';
};

export const findPrecedingHeadingText = (root: Node, path: number[]): string => {
  let current = [...path];
  while (current.length > 0) {
    const parent = Node.get(root, current.slice(0, -1));
    const children = SlateElement.isElement(parent) ? parent.children : [];
    for (let i = current[current.length - 1] - 1; i >= 0; i--) {
      const text = lastHeadingText(children[i]);
      if (text) return text;
    }
    current = current.slice(0, -1);
  }
  return '';
};

export const hasText = (node: Node): boolean => {
  for (const [n] of Node.texts(node)) {
    if (n.text) {
      return true;
    }
  }
  return false;
};
